import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';

const faqData = [
  {
    q: "Qu'est-ce qu'un BSO (brise-soleil orientable) ?",
    a: "Le BSO est un store à lames orientables en aluminium, généralement installé devant une fenêtre ou une baie vitrée. Il permet de moduler la lumière et la chaleur en orientant les lames, tout en préservant la vue vers l'extérieur. C'est une solution de protection solaire très performante, très répandue à Lyon.",
  },
  {
    q: "Mon BSO fait du bruit quand il fonctionne, est-ce normal ?",
    a: "Un léger bruit de moteur est normal. En revanche, des grincements, claquements ou bruits anormaux indiquent un problème : lames désaxées, moteur fatigué, ou mécanisme d'orientation encrassé. Un diagnostic rapide permet d'éviter que le problème ne s'aggrave.",
  },
  {
    q: "Réparez-vous les BSO de toutes les marques ?",
    a: "Oui, nous intervenons sur les BSO de toutes marques : Bubendorff, Profalux, Somfy, Franciaflex, etc. Nos techniciens connaissent les spécificités techniques de chaque fabricant pour un diagnostic précis.",
  },
];

export default function ReparationBSO() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Réparation de BSO à Lyon (brise-soleil orientable)',
    provider: { '@type': 'HomeAndConstructionBusiness', name: 'DFPRO Lyon', telephone: '+33980803684' },
    areaServed: [{ '@type': 'City', name: 'Lyon' }, { '@type': 'AdministrativeArea', name: 'Métropole de Lyon' }],
    description: 'Réparation de brise-soleil orientable (BSO) à Lyon : lames, moteur, télécommande, réglage. Devis gratuit, garantie 10 ans.',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://depanfenetres-lyon.fr/' },
      { '@type': 'ListItem', position: 2, name: 'Réparation store à Lyon', item: 'https://depanfenetres-lyon.fr/reparation-store-lyon/' },
      { '@type': 'ListItem', position: 3, name: 'Réparation BSO', item: 'https://depanfenetres-lyon.fr/reparation-bso-lyon/' },
    ],
  };

  return (
    <>
      <SEOHead
        title="Réparation de BSO à Lyon (brise-soleil) | DFPRO"
        description="BSO en panne à Lyon ? Lames orientables bloquées, moteur HS, télécommande. Réparation brise-soleil orientable, 24-48h, 7j/7. Devis gratuit. DFPRO Lyon."
        canonicalUrl="https://depanfenetres-lyon.fr/reparation-bso-lyon/"
        ogTitle="Réparation de BSO à Lyon — Brise-soleil orientable | DFPRO"
        ogDescription="BSO bloqué, lames, moteur ? Réparation brise-soleil à Lyon. Devis gratuit, garantie 10 ans."
        jsonLd={{ ...serviceJsonLd, ...faqJsonLd, ...breadcrumbJsonLd }}
      />

      <Header />

      <main>
        <div className="pt-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-xs text-secondary-400">
              <a href="/" className="hover:text-primary-500 transition-colors">Accueil</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <a href="/reparation-store-lyon/" className="hover:text-primary-500 transition-colors">Réparation store à Lyon</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-foreground-700 font-medium">Réparation BSO</span>
            </nav>
          </div>
        </div>

        <section className="relative w-full min-h-[380px] md:min-h-[420px] flex items-center bg-background-50">
          <div className="absolute inset-0">
            <img
              src="/images/bso-rep-hero-2026.jpg"
              alt="Réparation de BSO (brise-soleil orientable) à Lyon par DFPRO Lyon"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/50 via-foreground-900/35 to-foreground-900/50"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 py-14 md:py-16">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading font-bold text-2xl md:text-4xl text-background-50 leading-tight">
                Réparation de brise-soleil orientable (BSO) à Lyon
              </h1>
              <p className="mt-4 md:mt-5 text-sm md:text-lg text-background-50/90 max-w-2xl mx-auto leading-relaxed">
                Votre BSO est bloqué, les lames ne s'orientent plus ou le moteur ne répond pas ? Nos techniciens réparent votre brise-soleil orientable à Lyon.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="tel:+33980803684" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm md:text-base px-6 py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer shadow-[0_2px_8px_rgba(242,140,40,0.4)]">
                  <i className="ri-phone-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                  Appeler — 09 80 80 36 84
                </a>
              </div>
            </div>
          </div>
        </section>

        <ReassuranceBar />

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
              <div className="lg:col-span-2">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-800 mb-5">
                  BSO en panne à Lyon : diagnostic et réparation
                </h2>
                <div className="space-y-4 text-sm md:text-base text-secondary-500 leading-relaxed">
                  <p>
                    Le brise-soleil orientable, plus connu sous l'acronyme BSO, est un store à lames en aluminium qui équipe de nombreuses façades dans la Métropole de Lyon. Placé devant les fenêtres et baies vitrées, il protège efficacement de la chaleur tout en laissant passer la lumière. Mais quand il tombe en panne, c'est tout le confort de votre logement qui est impacté.
                  </p>
                  <p>
                    Les pannes de BSO les plus courantes que nous rencontrons à Lyon sont : <strong className="text-foreground-800">lames qui ne s'orientent plus</strong> (le mécanisme d'inclinaison est bloqué ou cassé), <strong className="text-foreground-800">lames désaxées ou sorties de leurs guides</strong> (problème de crémaillère ou de glissière latérale), <strong className="text-foreground-800">moteur qui ne répond plus</strong> (moteur HS, condensateur, télécommande désynchronisée), et <strong className="text-foreground-800">BSO qui fait du bruit</strong> en fonctionnement (mécanisme encrassé ou usé).
                  </p>
                  <p>
                    La bonne nouvelle, c'est que <strong className="text-foreground-800">la quasi-totalité des pannes de BSO sont réparables</strong>. Une lame tordue peut être remplacée individuellement, un moteur défaillant peut être changé sans démonter l'ensemble du store, et une télécommande peut être reprogrammée. Il est rarement nécessaire de remplacer tout le BSO.
                  </p>
                  <p>
                    Nos techniciens maîtrisent les BSO de toutes les marques présentes sur le marché : <strong className="text-foreground-800">Bubendorff, Profalux, Franciaflex, Somfy</strong>. Chaque fabricant a ses spécificités techniques que nous connaissons bien, ce qui nous permet d'établir un diagnostic rapide et de disposer des pièces détachées adaptées.
                  </p>
                  <p>
                    Nous intervenons à Lyon, Villeurbanne, Caluire-et-Cuire, Vénissieux, Bron, Vaulx-en-Velin, Saint-Priest et toutes les communes de la métropole. Chaque réparation de BSO bénéficie de notre <strong className="text-foreground-800">garantie 10 ans</strong>.
                  </p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 md:p-6 h-fit">
                <h3 className="font-heading font-semibold text-base text-foreground-800 mb-4">Nos services store</h3>
                <nav className="space-y-2">
                  {[
                    { label: 'Réparation store à Lyon (pilier)', href: '/reparation-store-lyon/' },
                    { label: 'Réparation store banne', href: '/reparation-store-banne-lyon/' },
                    { label: 'Store banne bloqué', href: '/store-banne-bloque-lyon/' },
                    { label: 'Motorisation de store', href: '/motorisation-store-lyon/' },
                    { label: 'Store électrique', href: '/store-electrique-lyon/' },
                  ].map((link, idx) => (
                    <a key={idx} href={link.href} className="flex items-center gap-2 text-sm text-secondary-500 hover:text-primary-500 transition-colors py-1.5 cursor-pointer">
                      <i className="ri-arrow-right-s-line text-primary-400 w-4 h-4 flex items-center justify-center"></i>
                      {link.label}
                    </a>
                  ))}
                </nav>
                <div className="mt-5 pt-5 border-t border-background-200">
                  <a href="tel:+33980803684" className="inline-flex items-center justify-center gap-2 w-full bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm py-3 rounded-md transition-colors cursor-pointer">
                    <i className="ri-phone-fill w-4 h-4 flex items-center justify-center"></i>
                    Appeler — 09 80 80 36 84
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-800">Questions fréquentes</h2>
            </div>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm md:text-base text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner
          title="BSO en panne à Lyon ?"
          subtitle="Appelez le 09 80 80 36 84. Diagnostic et réparation rapide de votre brise-soleil orientable."
          buttonText="Appeler maintenant"
          secondaryButtonText="Demander un devis"
          secondaryButtonHref="/contact/"
        />
      </main>
      <Footer />
    </>
  );
}